/**
 * SRTAnalytics.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Clinical analytics panel — sits above the session list in SRTHistoryPanel.
 * Requires ≥ 1 SRT session to render anything meaningful.
 *
 * Sections:
 *   1. INTERVENTION SIGNAL  — synthesised top-level recommendation (always first)
 *   2. OVERALL TREND        — trajectory arrow + sparkline + session bar chart
 *   3. DOMAIN FLAGS         — red/amber/green per kosha with clinical rationale
 *   4. DISPARITY ALERT      — flags when one kosha is ≥20pts behind child's own avg
 *   5. PROGRESS DELTA       — latest vs previous session, per kosha change bars
 *   6. STAGNATION ALERT     — warns if score flat across 3+ consecutive sessions
 *   7. STRENGTHS            — positive highlights when overall ≥65%
 *
 * Props:
 *   sessions  — SRTSession[], newest-first, each with koshaScores + overallScore
 *   KOSHAS    — kosha definition array ({ id, label, short, icon, color, bg })
 *   childName — string (used in narrative text)
 * ─────────────────────────────────────────────────────────────────────────────
 */

const C = {
    navy:        "#0B1F35", blue:        "#1A4F7A", sky:         "#2C7BB6",
    ink:         "#182430", muted:       "#5C7080", border:      "#DDE4EC", fog: "#F2F5F8",
    green:       "#1A7A40", greenBg:     "#E8F5EE", greenBorder: "#A8D5B5",
    amber:       "#B06000", amberBg:     "#FEF3E2", amberBorder: "#F0C070",
    red:         "#C0392B", redBg:       "#FDECEA", redBorder:   "#E8A09A",
  };
  
  // ── Pure analytics helpers ────────────────────────────────────────────────────
  
  function ascSessions(sessions) { return [...sessions].reverse(); }
  
  function overallOf(sess) {
    if (sess.overallScore != null && sess.overallScore > 0) return sess.overallScore;
    if (!sess.koshaScores) return 0;
    const vals = Object.values(sess.koshaScores);
    return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : 0;
  }
  
  function regressionSlope(arr) {
    if (arr.length < 2) return 0;
    const n = arr.length, xs = arr.map((_, i) => i);
    const mx = xs.reduce((a, b) => a + b, 0) / n;
    const my = arr.reduce((a, b) => a + b, 0) / n;
    const num = xs.reduce((s, x, i) => s + (x - mx) * (arr[i] - my), 0);
    const den = xs.reduce((s, x) => s + (x - mx) ** 2, 0);
    return den === 0 ? 0 : num / den;
  }
  
  function trendLabel(sl) {
    if (sl >  3) return { icon: "📈", label: "Strong Progress", color: C.green, bg: C.greenBg };
    if (sl >  0) return { icon: "↗️",  label: "Improving",       color: C.green, bg: C.greenBg };
    if (sl === 0)return { icon: "→",  label: "Stable",           color: C.amber, bg: C.amberBg };
    if (sl > -3) return { icon: "↘️",  label: "Slight Decline",  color: C.amber, bg: C.amberBg };
    return             { icon: "📉", label: "Declining",         color: C.red,   bg: C.redBg   };
  }
  
  function koshaFlagFor(pct) {
    if (pct >= 65) return { flag: "green", color: C.green, bg: C.greenBg };
    if (pct >= 40) return { flag: "amber", color: C.amber, bg: C.amberBg };
    return               { flag: "red",   color: C.red,   bg: C.redBg   };
  }
  
  function findDisparities(koshaScores, KOSHAS) {
    if (!koshaScores) return [];
    const vals = KOSHAS.map(k => koshaScores[k.id] ?? 0);
    const avg  = vals.reduce((a, b) => a + b, 0) / vals.length;
    return KOSHAS
      .map((k, i) => ({ kosha: k, pct: vals[i], gap: avg - vals[i] }))
      .filter(d => d.gap >= 20)
      .sort((a, b) => b.gap - a.gap);
  }
  
  function koshaDelta(latest, prev, KOSHAS) {
    if (!prev?.koshaScores || !latest?.koshaScores) return [];
    return KOSHAS.map(k => ({
      kosha: k,
      curr:  latest.koshaScores[k.id] ?? 0,
      prev:  prev.koshaScores[k.id]   ?? 0,
      delta: (latest.koshaScores[k.id] ?? 0) - (prev.koshaScores[k.id] ?? 0),
    }));
  }
  
  function findStagnation(sessions, KOSHAS) {
    if (sessions.length < 3) return [];
    const last3 = ascSessions(sessions).slice(-3);
    return KOSHAS.filter(k => {
      const vals = last3.map(s => s.koshaScores?.[k.id] ?? 0);
      const range = Math.max(...vals) - Math.min(...vals);
      return range <= 4 && vals[0] < 65;
    });
  }
  
  function buildInterventionSignal(sessions, KOSHAS) {
    if (!sessions.length) return null;
    const latest     = sessions[0];
    const overall    = overallOf(latest);
    const redKoshas  = KOSHAS.filter(k => (latest.koshaScores?.[k.id] ?? 0) < 40);
    const disparities = findDisparities(latest.koshaScores, KOSHAS);
    const sl         = regressionSlope(ascSessions(sessions).map(overallOf));
    const stagnant   = findStagnation(sessions, KOSHAS);
  
    if (overall < 30 || redKoshas.length >= 3) return {
      level: "critical", icon: "🚨",
      color: C.red, bg: C.redBg, border: C.redBorder,
      headline: "Immediate Intervention Recommended",
      body: `Scores are significantly below expected developmental milestones across ${redKoshas.length} or more domains. A specialist referral and individualised support plan should be initiated without delay.`,
      actions: [
        "Refer to special educator or developmental paediatrician",
        "Initiate or urgently review the IEP (Individualised Education Plan)",
        "Consider allied health referral — OT, SLT, or physiotherapy as appropriate",
        "Increase assessment frequency and adapt all classroom materials",
      ],
    };
  
    if (sl < -2 && redKoshas.length >= 1) return {
      level: "high", icon: "⚠️",
      color: C.red, bg: C.redBg, border: C.redBorder,
      headline: "Declining Trajectory — Intervention Needed",
      body: `Scores are declining across sessions and ${redKoshas.map(k => k.label).join(", ")} ${redKoshas.length === 1 ? "is" : "are"} in the critical range. Current strategies may be insufficient.`,
      actions: [
        `Intensify focus on ${redKoshas.map(k => k.short).join(" & ")} domain(s)`,
        "Review and adapt current teaching strategies with the support team",
        "Discuss regression with parents and document environmental factors",
        "Re-assess in 4–6 weeks",
      ],
    };
  
    if (disparities.length >= 2) return {
      level: "moderate", icon: "📋",
      color: C.amber, bg: C.amberBg, border: C.amberBorder,
      headline: "Targeted Support Recommended — Uneven Profile",
      body: `${disparities.map(d => d.kosha.label).join(" and ")} are significantly behind this child's own developmental average. Uneven profiles respond well to targeted domain-specific intervention.`,
      actions: [
        `Design specific activities for ${disparities[0].kosha.label}`,
        "Use UDL strategies aligned to the lagging domain's modality",
        "Re-screen affected domains in 6–8 weeks",
      ],
    };
  
    if (stagnant.length >= 2) return {
      level: "moderate", icon: "⏸️",
      color: C.amber, bg: C.amberBg, border: C.amberBorder,
      headline: "Stagnation Detected — Strategy Review Needed",
      body: `${stagnant.map(k => k.label).join(", ")} ${stagnant.length === 1 ? "has" : "have"} shown minimal change across the last 3 sessions. Current approaches may need refreshing.`,
      actions: [
        "Try alternate activity modalities for stagnant domains",
        "Review sensory and accessibility accommodations",
        "Consult with parents about home environment and routine factors",
      ],
    };
  
    if (overall >= 65 && sl >= 0) return {
      level: "positive", icon: "✅",
      color: C.green, bg: C.greenBg, border: C.greenBorder,
      headline: "On Track — Continue Current Approach",
      body: `The child is developing well and scores are ${sl > 1 ? "improving steadily" : "stable at a good level"}. No immediate intervention changes required.`,
      actions: [
        "Continue enrichment activities across all domains",
        "Re-assess in 3 months",
        sl > 2 ? "Document successful strategies in case notes" : "Monitor for any regression",
      ],
    };
  
    return {
      level: "monitor", icon: "👁️",
      color: C.amber, bg: C.amberBg, border: C.amberBorder,
      headline: "Monitor & Provide Structured Support",
      body: "Development is progressing but some domains need closer attention. Continue structured support and re-assess in 8–10 weeks.",
      actions: [
        "Maintain current support structures",
        "Focus classroom strategies on amber-flagged domains",
        "Discuss progress with parents at next review meeting",
      ],
    };
  }
  
  // ── Sparkline SVG ─────────────────────────────────────────────────────────────
  function Sparkline({ values, color = C.sky, width = 120, height = 36 }) {
    if (values.length < 2) return null;
    const min = 0, max = 100, range = max - min;
    const pts = values.map((v, i) => {
      const x = (i / (values.length - 1)) * width;
      const y = height - ((v - min) / range) * height;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(" ");
    const lv = values[values.length - 1];
    const lx = width;
    const ly = height - ((lv - min) / range) * height;
    return (
      <svg width={width} height={height} style={{ overflow: "visible" }}>
        <polyline points={pts} fill="none" stroke={color} strokeWidth={2.5}
          strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={lx} cy={ly} r={4} fill={color} />
        {values.map((v, i) => {
          if (i === values.length - 1) return null;
          const x = (i / (values.length - 1)) * width;
          const y = height - ((v - min) / range) * height;
          return <circle key={i} cx={x} cy={y} r={2.5} fill={color} opacity={0.5} />;
        })}
      </svg>
    );
  }
  
  // ── Section wrapper ───────────────────────────────────────────────────────────
  function Section({ title, icon, children }) {
    return (
      <div style={{ background: "white", borderRadius: 14,
        border: `1.5px solid ${C.border}`,
        boxShadow: "0 1px 5px rgba(0,0,0,.05)",
        overflow: "hidden", marginBottom: 12 }}>
        <div style={{ padding: "11px 16px", borderBottom: `1.5px solid ${C.border}`,
          background: C.fog, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 15 }}>{icon}</span>
          <span style={{ fontWeight: 800, fontSize: 13, color: C.ink }}>{title}</span>
          <div style={{ flex: 1, height: 1, background: C.border, marginLeft: 6 }} />
        </div>
        <div style={{ padding: "14px 16px" }}>{children}</div>
      </div>
    );
  }
  
  // ── MAIN EXPORT ───────────────────────────────────────────────────────────────
  export default function SRTAnalytics({ sessions, KOSHAS, childName }) {
    if (!sessions || sessions.length === 0) return null;
  
    const asc         = ascSessions(sessions);
    const latest      = sessions[0];
    const prev        = sessions[1] || null;
    const overalls    = asc.map(overallOf);
    const sl          = regressionSlope(overalls);
    const trend       = trendLabel(sl);
    const overall     = overallOf(latest);
    const disparities = findDisparities(latest.koshaScores, KOSHAS);
    const deltas      = koshaDelta(latest, prev, KOSHAS);
    const stagnant    = findStagnation(sessions, KOSHAS);
    const signal      = buildInterventionSignal(sessions, KOSHAS);
    const hasPrev     = sessions.length >= 2;
    const fmt         = s => new Date(s.date || s.createdAt)
      .toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });
  
    // Clinical rationale per kosha per flag
    const RATIONALE = {
      annamaya: {
        red:   "Motor and physical delays may limit independence in daily tasks, fine motor activities, and full classroom participation. Physiotherapy or OT referral should be considered.",
        amber: "Some motor milestones need targeted practice. Fine or gross motor activities should be incorporated into the daily routine.",
        green: "Physical development is progressing well. Continue gross and fine motor enrichment.",
      },
      pranamaya: {
        red:   "Significant socio-emotional difficulties can impede peer interaction, learning readiness, and self-regulation — foundational to all other domains. Behavioural support or counselling referral warranted.",
        amber: "Emotional regulation or social skills need support. Social stories, role play, and structured peer interaction are recommended.",
        green: "Socio-emotional development is healthy. Maintain supportive peer learning environments.",
      },
      manomaya: {
        red:   "Critical gaps in attention, working memory, or reasoning will affect academic learning across all subjects. Cognitive scaffolding and occupational therapy assessment recommended.",
        amber: "Working memory or reasoning tasks need scaffolding. Use chunking, visual supports, and repetition strategies.",
        green: "Cognitive skills are developing well. Challenge with increasingly complex reasoning tasks.",
      },
      vijnanamaya: {
        red:   "Language, literacy, and numeracy are the academic foundation — delays here compound across the entire curriculum and widen over time. Speech-language therapy and intensive literacy support are urgent priorities.",
        amber: "Early literacy or numeracy milestones need focused support. Phonological awareness activities, number sense games, and structured language sessions are recommended.",
        green: "Language and early academic skills are on track. Continue exposure to rich language, books, and number activities.",
      },
      anandamaya: {
        red:   "Severely limited creative-aesthetic engagement may signal disengagement, restricted play schemas, or emotional withdrawal. Expressive arts therapy and play-based intervention should be explored.",
        amber: "Creative expression and play quality could be enriched. Provide more unstructured creative time, arts materials, and imaginative play scenarios.",
        green: "Creative engagement is active and positive. Use creative strengths to scaffold learning in other domains.",
      },
    };
  
    return (
      <div style={{ marginBottom: 24 }}>
  
        {/* ══ 1. INTERVENTION SIGNAL ══════════════════════════════════════════ */}
        {signal && (
          <div style={{ background: signal.bg,
            border: `2px solid ${signal.border || signal.color + "44"}`,
            borderRadius: 14, padding: "16px 18px", marginBottom: 12,
            boxShadow: `0 4px 18px ${signal.color}14` }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
              <div style={{ fontSize: 28, flexShrink: 0, lineHeight: 1 }}>{signal.icon}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 900, fontSize: 15, color: signal.color, marginBottom: 5 }}>
                  {signal.headline}
                </div>
                <div style={{ fontSize: 12, color: C.ink, lineHeight: 1.8, marginBottom: 10 }}>
                  {signal.body}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  {signal.actions.map((a, i) => (
                    <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start",
                      fontSize: 12, color: C.ink }}>
                      <span style={{ color: signal.color, fontWeight: 900, flexShrink: 0, marginTop: 1 }}>
                        {i + 1}.
                      </span>
                      <span style={{ lineHeight: 1.6 }}>{a}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
  
        {/* ══ 2. OVERALL TREND ════════════════════════════════════════════════ */}
        <Section title="Overall Trajectory" icon="📈">
          <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            {/* Big score */}
            <div style={{ textAlign: "center", flexShrink: 0 }}>
              <div style={{ fontSize: 42, fontWeight: 900, color: trend.color,
                fontFamily: "Georgia, serif", lineHeight: 1 }}>{overall}%</div>
              <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>latest overall</div>
            </div>
  
            <div style={{ width: 1, height: 52, background: C.border, flexShrink: 0 }} />
  
            {/* Trend + sparkline */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1, minWidth: 160 }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 6,
                background: trend.bg, color: trend.color, fontSize: 12, fontWeight: 800,
                padding: "5px 12px", borderRadius: 20, alignSelf: "flex-start" }}>
                {trend.icon} {trend.label}
              </div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 10 }}>
                <Sparkline values={overalls} color={trend.color} width={150} height={40} />
                <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.6 }}>
                  {sessions.length} assessment{sessions.length !== 1 ? "s" : ""}
                  {sessions.length >= 2 && (
                    <><br />{fmt(asc[0])} → {fmt(asc[asc.length - 1])}</>
                  )}
                </div>
              </div>
            </div>
  
            {/* Bar chart per session */}
            {sessions.length > 1 && (
              <div style={{ display: "flex", gap: 6, alignItems: "flex-end",
                flexWrap: "wrap", flex: 1, minWidth: 100 }}>
                {asc.map((s, i) => {
                  const o  = overallOf(s);
                  const h  = Math.max(16, Math.round((o / 100) * 56));
                  const fc = koshaFlagFor(o).color;
                  return (
                    <div key={i} style={{ display: "flex", flexDirection: "column",
                      alignItems: "center", gap: 3 }}>
                      <div style={{ fontSize: 9, fontWeight: 800, color: fc }}>{o}%</div>
                      <div style={{ width: 20, height: h, borderRadius: 4, background: fc }} />
                      <div style={{ fontSize: 7, color: C.muted, maxWidth: 28,
                        textAlign: "center", lineHeight: 1.2 }}>{fmt(s)}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Section>
  
        {/* ══ 3. DOMAIN FLAGS ═════════════════════════════════════════════════ */}
        <Section title="Domain Flags — Latest Session" icon="🚦">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 8 }}>
            {KOSHAS.map(k => {
              const pct  = latest.koshaScores?.[k.id] ?? 0;
              const { flag, color, bg } = koshaFlagFor(pct);
              const flagIcon = flag === "green" ? "✅" : flag === "amber" ? "⚠️" : "🔴";
              const flagText = flag === "green" ? "On Track" : flag === "amber" ? "Monitor" : "Needs Support";
              const rationale = RATIONALE[k.id]?.[flag] || "";
              return (
                <div key={k.id} style={{ background: bg, borderRadius: 11,
                  padding: "12px 10px", border: `1.5px solid ${color}33`, textAlign: "center" }}>
                  <div style={{ fontSize: 22, marginBottom: 4 }}>{k.icon}</div>
                  <div style={{ fontSize: 24, fontWeight: 900, color,
                    fontFamily: "Georgia, serif", lineHeight: 1 }}>{pct}%</div>
                  <div style={{ fontSize: 9, fontWeight: 700, color: k.color,
                    marginTop: 3, marginBottom: 5 }}>{k.short || k.label}</div>
                  <div style={{ fontSize: 9, fontWeight: 800, color,
                    background: "rgba(255,255,255,.65)", borderRadius: 6,
                    padding: "2px 6px", display: "inline-block", marginBottom: 7 }}>
                    {flagIcon} {flagText}
                  </div>
                  <div style={{ fontSize: 9, color: C.ink, lineHeight: 1.55,
                    textAlign: "left" }}>{rationale}</div>
                </div>
              );
            })}
          </div>
        </Section>
  
        {/* ══ 4. DISPARITY ALERT ══════════════════════════════════════════════ */}
        {disparities.length > 0 && (
          <Section title="Profile Disparity Alert" icon="📊">
            <div style={{ background: C.amberBg, border: `1px solid ${C.amberBorder}`,
              borderRadius: 10, padding: "10px 14px", marginBottom: 12,
              fontSize: 12, color: C.amber, lineHeight: 1.7 }}>
              <strong>Uneven developmental profile detected.</strong> The{" "}
              {disparities.length === 1 ? "domain below is" : "domains below are"} significantly
              behind this child's own average — suggesting a targeted domain deficit rather
              than general global delay. Focused intervention in the lagging area(s) is recommended.
            </div>
  
            {/* Bar chart all koshas vs own average */}
            {(() => {
              const vals = KOSHAS.map(k => latest.koshaScores?.[k.id] ?? 0);
              const avg  = Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
              return (
                <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                  {KOSHAS.map((k, i) => {
                    const pct = vals[i];
                    const gap = avg - pct;
                    const isFlag = gap >= 20;
                    const isWatch = gap >= 10 && gap < 20;
                    const barColor = isFlag ? C.red : pct >= 65 ? C.green : C.amber;
                    return (
                      <div key={k.id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: 14, flexShrink: 0 }}>{k.icon}</span>
                        <span style={{ fontSize: 11, fontWeight: 700, color: k.color,
                          minWidth: 88, flexShrink: 0 }}>{k.short || k.label}</span>
                        <div style={{ flex: 1, position: "relative", height: 10,
                          background: C.fog, borderRadius: 5, overflow: "hidden" }}>
                          {/* Average reference */}
                          <div style={{ position: "absolute", left: `${avg}%`, top: 0,
                            bottom: 0, width: 2, background: C.muted, zIndex: 2,
                            transform: "translateX(-50%)" }} />
                          <div style={{ width: `${pct}%`, height: "100%",
                            background: barColor, borderRadius: 5 }} />
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 900, color: barColor,
                          minWidth: 32, textAlign: "right" }}>{pct}%</span>
                        {isFlag ? (
                          <span style={{ fontSize: 9, background: C.redBg, color: C.red,
                            fontWeight: 800, padding: "1px 7px", borderRadius: 8,
                            border: `1px solid ${C.redBorder}`, flexShrink: 0, minWidth: 90 }}>
                            ▼ {Math.round(gap)}pts below avg
                          </span>
                        ) : isWatch ? (
                          <span style={{ fontSize: 9, background: C.amberBg, color: C.amber,
                            fontWeight: 700, padding: "1px 7px", borderRadius: 8, flexShrink: 0,
                            minWidth: 90 }}>
                            ▼ {Math.round(gap)}pts
                          </span>
                        ) : (
                          <span style={{ minWidth: 90 }} />
                        )}
                      </div>
                    );
                  })}
                  <div style={{ fontSize: 10, color: C.muted, marginTop: 4, fontStyle: "italic" }}>
                    Vertical line = child's own average ({avg}%). Domains ≥20pts below are flagged.
                  </div>
                </div>
              );
            })()}
          </Section>
        )}
  
        {/* ══ 5. PROGRESS DELTA ═══════════════════════════════════════════════ */}
        {hasPrev && (
          <Section title={`Progress: ${fmt(prev)} → ${fmt(latest)}`} icon="🔄">
            {/* Overall delta hero */}
            {(() => {
              const currO = overallOf(latest), prevO = overallOf(prev);
              const d = currO - prevO;
              const dc = d > 0 ? C.green : d < 0 ? C.red : C.amber;
              const db = d > 0 ? C.greenBg : d < 0 ? C.redBg : C.amberBg;
              return (
                <div style={{ display: "flex", alignItems: "center", gap: 12,
                  marginBottom: 14, padding: "11px 14px", background: db,
                  borderRadius: 10, border: `1px solid ${dc}33` }}>
                  <span style={{ fontSize: 28, fontWeight: 900, color: dc,
                    fontFamily: "Georgia, serif", lineHeight: 1 }}>
                    {d > 0 ? "+" : ""}{d}%
                  </span>
                  <div style={{ fontSize: 12, color: C.ink, lineHeight: 1.7, flex: 1 }}>
                    Overall: <strong>{prevO}% → {currO}%</strong>.{" "}
                    {d > 5  ? "Significant improvement — current strategies are working well." :
                     d > 0  ? "Modest improvement — continue and sustain current support." :
                     d === 0 ? "No change since last session — review strategies." :
                     d > -5 ? "Slight decline — check for environmental or attendance factors." :
                               "Notable decline — convene support team review promptly."}
                  </div>
                </div>
              );
            })()}
  
            {/* Per-kosha delta rows */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {deltas.map(({ kosha: k, curr, prev: p, delta: d }) => {
                const dc = d > 3 ? C.green : d > 0 ? "#1A9A55" : d === 0 ? C.amber : d > -5 ? "#D06000" : C.red;
                const di = d > 3 ? "↑↑" : d > 0 ? "↑" : d === 0 ? "→" : d > -5 ? "↓" : "↓↓";
                const db = d > 0 ? C.greenBg : d < 0 ? C.redBg : C.amberBg;
                return (
                  <div key={k.id} style={{ display: "flex", alignItems: "center", gap: 8,
                    padding: "8px 10px", background: C.fog, borderRadius: 8,
                    border: `1px solid ${C.border}` }}>
                    <span style={{ fontSize: 15, flexShrink: 0 }}>{k.icon}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: k.color,
                      minWidth: 80, flexShrink: 0 }}>{k.short || k.label}</span>
                    {/* Before/after bar */}
                    <div style={{ flex: 1, position: "relative", height: 9,
                      background: "#E0E8F0", borderRadius: 4, overflow: "hidden" }}>
                      <div style={{ position: "absolute", inset: 0, width: `${Math.min(p, curr)}%`,
                        background: k.color, opacity: .7, borderRadius: 4 }} />
                      {d > 0 && (
                        <div style={{ position: "absolute", top: 0, bottom: 0,
                          left: `${p}%`, width: `${d}%`,
                          background: k.color, borderRadius: "0 4px 4px 0" }} />
                      )}
                      {d < 0 && (
                        <div style={{ position: "absolute", top: 0, bottom: 0,
                          left: `${curr}%`, width: `${Math.abs(d)}%`,
                          background: C.red, opacity: .5, borderRadius: "0 4px 4px 0" }} />
                      )}
                    </div>
                    <span style={{ fontSize: 10, color: C.muted, minWidth: 26, textAlign: "right" }}>
                      {p}%
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 900, color: dc, minWidth: 18,
                      textAlign: "center" }}>{di}</span>
                    <span style={{ fontSize: 11, fontWeight: 900, color: dc,
                      minWidth: 30, textAlign: "right" }}>{curr}%</span>
                    <span style={{ fontSize: 10, fontWeight: 800, color: dc,
                      background: db, padding: "1px 7px", borderRadius: 7,
                      minWidth: 40, textAlign: "center", border: `1px solid ${dc}33` }}>
                      {d > 0 ? "+" : ""}{d}
                    </span>
                  </div>
                );
              })}
            </div>
          </Section>
        )}
  
        {/* ══ 6. STAGNATION ALERT ═════════════════════════════════════════════ */}
        {stagnant.length > 0 && (
          <Section title="Stagnation Detected" icon="⏸️">
            <div style={{ background: C.amberBg, border: `1px solid ${C.amberBorder}`,
              borderRadius: 10, padding: "12px 14px", marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: C.amber, marginBottom: 4 }}>
                Minimal change across the last 3 sessions
              </div>
              <div style={{ fontSize: 12, color: C.ink, lineHeight: 1.7 }}>
                {stagnant.map(k => k.label || k.short).join(", ")}{" "}
                {stagnant.length === 1 ? "has" : "have"} shown less than 4 percentage points
                of change across three consecutive assessments despite not being in the achieved range.
                This may indicate that current teaching strategies need to be varied,
                or that external factors (attendance, health, environment) should be explored.
              </div>
            </div>
  
            {/* Stagnant kosha tiles with value history */}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
              {stagnant.map(k => {
                const recentVals = ascSessions(sessions).slice(-3)
                  .map(s => s.koshaScores?.[k.id] ?? 0);
                return (
                  <div key={k.id} style={{ background: "white", borderRadius: 10,
                    border: `1.5px solid ${C.amberBorder}`, padding: "10px 14px",
                    display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 180 }}>
                    <span style={{ fontSize: 20 }}>{k.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 800, fontSize: 12, color: k.color, marginBottom: 5 }}>
                        {k.label || k.short}
                      </div>
                      <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                        {recentVals.map((v, i) => (
                          <span key={i} style={{ fontSize: 11, fontWeight: 700, color: C.amber,
                            background: C.amberBg, padding: "2px 7px", borderRadius: 5 }}>
                            {v}%
                          </span>
                        ))}
                        {recentVals.length >= 2 && (
                          <span style={{ fontSize: 9, color: C.muted }}>
                            Δ{Math.abs(recentVals[recentVals.length - 1] - recentVals[0])}pts
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
  
            <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.8,
              background: C.fog, borderRadius: 8, padding: "10px 12px" }}>
              <strong>Suggested actions:</strong> Switch modalities (e.g. visual → tactile or
              kinaesthetic); introduce peer-mediated learning; consult parents about home
              practice routines; consider whether session duration or frequency is appropriate
              for this child's stamina and concentration span.
            </div>
          </Section>
        )}
  
        {/* ══ 7. STRENGTHS ════════════════════════════════════════════════════ */}
        {(() => {
          const strongKoshas = KOSHAS.filter(k => (latest.koshaScores?.[k.id] ?? 0) >= 72);
          const bigGain = deltas.length
            ? deltas.reduce((b, d) => d.delta > b.delta ? d : b, deltas[0])
            : null;
          if (!strongKoshas.length && !(bigGain?.delta >= 6)) return null;
          return (
            <Section title="Strengths to Build On" icon="🌟">
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 10, lineHeight: 1.6 }}>
                Strong domains can be used as <em>scaffolds</em> — present challenging tasks
                through the child's strengths to maintain engagement while building weaker areas.
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {strongKoshas.map(k => (
                  <div key={k.id} style={{ background: C.greenBg,
                    border: `1.5px solid ${C.greenBorder}`,
                    borderRadius: 10, padding: "10px 14px", flex: 1, minWidth: 130 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4 }}>
                      <span style={{ fontSize: 18 }}>{k.icon}</span>
                      <span style={{ fontWeight: 800, fontSize: 12, color: C.green }}>
                        {k.label || k.short}
                      </span>
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 900, color: C.green,
                      fontFamily: "Georgia, serif" }}>
                      {latest.koshaScores?.[k.id]}%
                    </div>
                    <div style={{ fontSize: 10, color: C.green, marginTop: 3 }}>
                      ✅ Leverage as a learning scaffold
                    </div>
                  </div>
                ))}
                {bigGain?.delta >= 6 && (
                  <div style={{ background: "#EBF4FA", border: "1.5px solid #BDD7EE",
                    borderRadius: 10, padding: "10px 14px", flex: 1, minWidth: 130 }}>
                    <div style={{ fontSize: 10, fontWeight: 800, color: C.sky, marginBottom: 4 }}>
                      BIGGEST GAIN THIS SESSION
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4 }}>
                      <span style={{ fontSize: 18 }}>{bigGain.kosha.icon}</span>
                      <span style={{ fontWeight: 800, fontSize: 12, color: C.blue }}>
                        {bigGain.kosha.label || bigGain.kosha.short}
                      </span>
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 900, color: C.sky,
                      fontFamily: "Georgia, serif" }}>+{bigGain.delta}%</div>
                    <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>
                      {bigGain.prev}% → {bigGain.curr}%
                    </div>
                  </div>
                )}
              </div>
            </Section>
          );
        })()}
  
      </div>
    );
  }